import { CardFormat, CardGenerationMode } from '../models/subscription-enums';

/**
 * PAUL-2019 — where a sold gift card's code comes from, configured per gift-card product.
 *
 * A null `generation_mode` means codes were never configured: the box office accepts any code
 * unchecked, web sales get a 12-character code and refund cards 15 digits, exactly as before the
 * feature existed. Choosing a mode is what switches the product over.
 *
 * Off-register mints — web sales, refund gift cards, digital delivery — ALWAYS generate and never
 * consume the pre-generated pool, whatever the mode. Pre-generated codes exist to back physical
 * plastic.
 */
export interface GiftCardCodesConfigModel {
  id: string;
  card_format: CardFormat | null;
  /** Sizes the generated body only — `card_prefix` is additive on top of it. */
  card_length: number | null;
  card_prefix: string | null;
  generation_mode: CardGenerationMode | null;
  /** Pre-generated codes not yet claimed by a sale. */
  available_count: number;
  /** Pre-generated codes already claimed by a sale. */
  used_count: number;
}

export interface GiftCardCodesConfigEditModel {
  card_format: CardFormat | null;
  card_length: number | null;
  card_prefix: string | null;
  generation_mode: CardGenerationMode | null;
}

export interface GiftCardCodeBatchModel {
  id: string;
  inserted?: string;
  /** {@link CardBatchType} — 0 generated, 1 uploaded. */
  mode: number;
  amount?: number;
  prefix?: string;
  /** Codes of this batch already claimed by a sale. */
  used_count?: number;
}

export enum GiftCardCodeImportKind {
  Duplicate = 'duplicate',
  InvalidFormat = 'invalid_format',
}

export interface GiftCardCodeImportDetail {
  line: number;
  code: string;
  kind: GiftCardCodeImportKind | string;
}

/**
 * An upload never fails on a bad row: a malformed code, or one that already exists anywhere in the
 * company, is skipped and counted while every valid row still imports. Drive the summary off the
 * counts — `details` is capped, so its length is not the number of skipped rows.
 */
export interface GiftCardCodeBatchResultModel extends GiftCardCodeBatchModel {
  total: number;
  imported: number;
  skipped_duplicate: number;
  skipped_invalid: number;
  details: GiftCardCodeImportDetail[];
  /** True when more rows were skipped than `details` could carry. */
  details_truncated: boolean;
}
